import { useState, useEffect, useRef, useCallback } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '../components/ui/dialog';
import { useAuth } from '../context/AuthContext';
import { toast } from 'sonner';
import { 
  Image, 
  Type, 
  Square, 
  Minus,
  Upload,
  Search,
  Trash2,
  Download,
  Save,
  Undo,
  Redo,
  ZoomIn,
  ZoomOut,
  Move,
  Palette,
  Loader2,
  Lock,
  Sparkles,
  Plus,
  X,
  ChevronUp,
  ChevronDown,
  Copy,
  LayoutTemplate,
  Share2,
  Link2,
  Check,
  Eye,
  Trash,
  Clock
} from 'lucide-react';
import { STARTER_TEMPLATES, COLOR_SCHEMES, applyColorScheme } from '../data/starterTemplates';
import { shareApi } from '../lib/api';

const API = process.env.REACT_APP_BACKEND_URL;

const ELEMENT_TYPES = {
  image: { icon: Image, label: 'Image' },
  text: { icon: Type, label: 'Text' },
  shape: { icon: Square, label: 'Shape' },
  divider: { icon: Minus, label: 'Divider' },
};

const PRESET_COLORS = [
  '#2F5233', '#1a365d', '#742a2a', '#744210', '#234e52',
  '#553c9a', '#702459', '#1a202c', '#2d3748', '#4a5568',
  '#ffffff', '#f7fafc', '#edf2f7', '#e2e8f0', '#cbd5e0'
];

export default function DesignStudio() {
  const { user, loading: authLoading } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const canvasRef = useRef(null);
  const importedRef = useRef(false);
  
  const [elements, setElements] = useState([]);
  const [selectedElement, setSelectedElement] = useState(null);
  const [isDragging, setIsDragging] = useState(false);
  const [dragOffset, setDragOffset] = useState({ x: 0, y: 0 });
  const [canvasSize, setCanvasSize] = useState({ width: 800, height: 1100 });
  const [backgroundColor, setBackgroundColor] = useState('#ffffff');
  const [zoom, setZoom] = useState(0.8);
  const [history, setHistory] = useState([]);
  const [historyIndex, setHistoryIndex] = useState(-1);
  
  // Stock images
  const [stockSearchQuery, setStockSearchQuery] = useState('');
  const [stockImages, setStockImages] = useState([]);
  const [searchingStock, setSearchingStock] = useState(false);
  
  // User images
  const [myImages, setMyImages] = useState([]);
  const [uploading, setUploading] = useState(false);
  
  // Saved templates
  const [savedTemplates, setSavedTemplates] = useState([]);
  const [templateName, setTemplateName] = useState('');
  const [saveDialogOpen, setSaveDialogOpen] = useState(false);
  const [saving, setSaving] = useState(false);
  const [activeColorScheme, setActiveColorScheme] = useState(null);
  const [shareDialogOpen, setShareDialogOpen] = useState(false);
  const [sharing, setSharing] = useState(false);
  const [shareLink, setShareLink] = useState(null);
  const [copied, setCopied] = useState(false);
  const [myShares, setMyShares] = useState([]);
  const [showMyShares, setShowMyShares] = useState(false);

  const isPro = user?.subscription_plan === 'pro' || user?.subscription_plan === 'grants_pro' || user?.subscription_plan === 'trial' || user?.is_lifetime_pro;

  useEffect(() => {
    if (authLoading) return;
    if (!user) {
      navigate('/login');
      return;
    }
    if (isPro) {
      fetchMyImages();
    }
    fetchSavedTemplates();
  }, [user, authLoading, navigate, isPro]);

  const fetchMyImages = async () => {
    try {
      const token = localStorage.getItem('pitchpal_token');
      const res = await fetch(`${API}/api/design/my-images`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (res.ok) {
        const data = await res.json();
        setMyImages(data);
      }
    } catch (error) {
      console.error('Error fetching images:', error);
    }
  };

  const fetchSavedTemplates = async () => {
    try {
      const token = localStorage.getItem('pitchpal_token');
      const res = await fetch(`${API}/api/design/templates`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (res.ok) {
        const data = await res.json();
        setSavedTemplates(data);
      }
    } catch (error) {
      console.error('Error fetching templates:', error);
    }
  };

  // Define saveToHistory before the useEffect that uses it
  const saveToHistory = useCallback((newElements) => {
    setHistory(prev => {
      const newHistory = prev.slice(0, historyIndex + 1);
      newHistory.push(JSON.stringify(newElements));
      return newHistory;
    });
    setHistoryIndex(prev => prev + 1);
  }, [historyIndex]);

  // Define parseContentToElements before the useEffect that uses it
  const parseContentToElements = useCallback((content, title) => {
    const newElements = [];
    let yPos = 40;
    const xPad = 50;
    const canvasW = canvasSize.width;
    const textWidth = canvasW - xPad * 2;
    const uid = () => Date.now().toString() + Math.random().toString(36).slice(2, 8);

    // Title bar
    newElements.push({
      id: uid(),
      type: 'shape',
      x: 0, y: 0,
      width: canvasW, height: 80,
      content: '',
      style: { backgroundColor: '#2F5233', color: '#ffffff', fontSize: 16, fontWeight: 'normal', textAlign: 'left', borderRadius: 0 }
    });
    newElements.push({
      id: uid(),
      type: 'text',
      x: xPad, y: 20,
      width: textWidth, height: 45,
      content: title || 'AI Generated Content',
      style: { backgroundColor: 'transparent', color: '#ffffff', fontSize: 26, fontWeight: 'bold', textAlign: 'left', borderRadius: 0 }
    });
    yPos = 100;

    // Divider
    newElements.push({
      id: uid(),
      type: 'divider',
      x: xPad, y: yPos,
      width: textWidth, height: 3,
      content: '',
      style: { backgroundColor: 'transparent', color: '#2F5233', fontSize: 16, fontWeight: 'normal', textAlign: 'left', borderRadius: 0 }
    });
    yPos += 20;

    const lines = content.split('\n');
    for (const line of lines) {
      const trimmed = line.trim();
      if (!trimmed) {
        yPos += 10;
        continue;
      }

      let fontSize = 13;
      let fontWeight = 'normal';
      let color = '#333333';
      let height = 28;
      let text = trimmed;

      if (trimmed.startsWith('### ')) {
        text = trimmed.slice(4).replace(/\*+/g, '').trim();
        fontSize = 16;
        fontWeight = 'bold';
        color = '#2F5233';
        height = 30;
        yPos += 8;
      } else if (trimmed.startsWith('## ')) {
        text = trimmed.slice(3).replace(/\*+/g, '').trim();
        fontSize = 19;
        fontWeight = 'bold';
        color = '#1a365d';
        height = 34;
        yPos += 12;
      } else if (trimmed.startsWith('# ')) {
        text = trimmed.slice(2).replace(/\*+/g, '').trim();
        fontSize = 22;
        fontWeight = 'bold';
        color = '#2F5233';
        height = 38;
        yPos += 14;
      } else if (trimmed.startsWith('- ') || trimmed.startsWith('* ')) {
        text = '\u2022 ' + trimmed.slice(2).replace(/\*\*(.*?)\*\*/g, '$1').replace(/\*(.*?)\*/g, '$1').trim();
        fontSize = 13;
        height = 26;
      } else {
        // Clean markdown bold/italic
        text = trimmed.replace(/\*\*(.*?)\*\*/g, '$1').replace(/\*(.*?)\*/g, '$1');
      }

      // Estimate line wrapping - roughly 8px per char at font size 13
      const charsPerLine = Math.floor(textWidth / (fontSize * 0.55));
      const wrappedLines = Math.ceil(text.length / charsPerLine);
      height = Math.max(height, wrappedLines * (fontSize + 6));

      newElements.push({
        id: uid(),
        type: 'text',
        x: xPad,
        y: yPos,
        width: textWidth,
        height: height,
        content: text,
        style: { backgroundColor: 'transparent', color, fontSize, fontWeight, textAlign: 'left', borderRadius: 0 }
      });

      yPos += height + 4;
    }

    // Extend canvas height if content is long
    if (yPos + 60 > canvasSize.height) {
      setCanvasSize(prev => ({ ...prev, height: yPos + 100 }));
    }

    return newElements;
  }, [canvasSize.width, canvasSize.height]);

  // Import content from AI Generator
  useEffect(() => {
    if (importedRef.current) return;
    const state = location.state;
    if (state?.importedContent) {
      importedRef.current = true;
      const imported = parseContentToElements(state.importedContent, state.contentTitle);
      setElements(imported);
      setBackgroundColor('#ffffff');
      saveToHistory(imported);
      toast.success(`Loaded "${state.contentTitle}" into Design Studio`);
      // Clear navigation state so refresh doesn't re-import
      window.history.replaceState({}, document.title);
    }
  }, [location.state, saveToHistory, parseContentToElements]);

  const searchStockImages = async () => {
    if (!stockSearchQuery.trim()) return;
    
    setSearchingStock(true);
    try {
      const token = localStorage.getItem('pitchpal_token');
      const res = await fetch(`${API}/api/design/stock-images?query=${encodeURIComponent(stockSearchQuery)}&count=12`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (res.ok) {
        const data = await res.json();
        setStockImages(data.images || []);
      }
    } catch (error) {
      toast.error('Failed to search images');
    } finally {
      setSearchingStock(false);
    }
  };

  const uploadImage = async (file) => {
    setUploading(true);
    try {
      const reader = new FileReader();
      reader.onload = async (e) => {
        const base64 = e.target.result;
        const token = localStorage.getItem('pitchpal_token');
        
        const res = await fetch(`${API}/api/design/upload-image`, {
          method: 'POST',
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json'
          },
          body: JSON.stringify({
            image_data: base64,
            filename: file.name
          })
        });
        
        if (res.ok) {
          const data = await res.json();
          toast.success('Image uploaded!');
          fetchMyImages();
          // Add to canvas
          addElement('image', `${API}${data.url}`);
        } else {
          const error = await res.json();
          toast.error(error.detail || 'Upload failed');
        }
        setUploading(false);
      };
      reader.readAsDataURL(file);
    } catch (error) {
      toast.error('Failed to upload image');
      setUploading(false);
    }
  };

  const undo = () => {
    if (historyIndex > 0) {
      setHistoryIndex(historyIndex - 1);
      setElements(JSON.parse(history[historyIndex - 1]));
    }
  };

  const redo = () => {
    if (historyIndex < history.length - 1) {
      setHistoryIndex(historyIndex + 1);
      setElements(JSON.parse(history[historyIndex + 1]));
    }
  };

  const addElement = (type, content = '') => {
    const existingCount = elements.length;
    const offsetX = 50 + (existingCount * 15) % 200;
    const offsetY = 50 + (existingCount * 15) % 200;
    
    const newElement = {
      id: Date.now().toString(),
      type,
      x: offsetX,
      y: offsetY,
      width: type === 'image' ? 300 : type === 'text' ? 200 : type === 'divider' ? 400 : 100,
      height: type === 'image' ? 200 : type === 'text' ? 50 : type === 'divider' ? 4 : 100,
      content: content || (type === 'text' ? 'Click to edit text' : ''),
      style: {
        backgroundColor: type === 'shape' ? '#e2e8f0' : 'transparent',
        color: '#1a202c',
        fontSize: 16,
        fontWeight: 'normal',
        textAlign: 'left',
        borderRadius: type === 'shape' ? 8 : 0,
      }
    };
    
    const newElements = [...elements, newElement];
    setElements(newElements);
    setSelectedElement(newElement.id);
    saveToHistory(newElements);
  };

  const updateElement = (id, updates) => {
    const newElements = elements.map(el => 
      el.id === id ? { ...el, ...updates } : el
    );
    setElements(newElements);
  };

  const deleteElement = (id) => {
    const newElements = elements.filter(el => el.id !== id);
    setElements(newElements);
    setSelectedElement(null);
    saveToHistory(newElements);
  };

  const bringForward = (id) => {
    const idx = elements.findIndex(el => el.id === id);
    if (idx < elements.length - 1) {
      const newElements = [...elements];
      [newElements[idx], newElements[idx + 1]] = [newElements[idx + 1], newElements[idx]];
      setElements(newElements);
    }
  };

  const sendBackward = (id) => {
    const idx = elements.findIndex(el => el.id === id);
    if (idx > 0) {
      const newElements = [...elements];
      [newElements[idx], newElements[idx - 1]] = [newElements[idx - 1], newElements[idx]];
      setElements(newElements);
    }
  };

  const duplicateElement = (id) => {
    const element = elements.find(el => el.id === id);
    if (element) {
      const newElement = {
        ...JSON.parse(JSON.stringify(element)),
        id: Date.now().toString() + Math.random().toString(36).slice(2),
        x: element.x + 20,
        y: element.y + 20
      };
      const newElements = [...elements, newElement];
      setElements(newElements);
      setSelectedElement(newElement.id);
      saveToHistory(newElements);
    }
  };

  const handleCanvasMouseDown = (e, elementId) => {
    if (elementId) {
      e.stopPropagation();
      setSelectedElement(elementId);
      const element = elements.find(el => el.id === elementId);
      if (element) {
        const rect = canvasRef.current.getBoundingClientRect();
        setDragOffset({
          x: (e.clientX - rect.left) / zoom - element.x,
          y: (e.clientY - rect.top) / zoom - element.y
        });
        setIsDragging(true);
      }
    } else {
      setSelectedElement(null);
    }
  };

  const handleCanvasMouseMove = (e) => {
    if (isDragging && selectedElement) {
      const rect = canvasRef.current.getBoundingClientRect();
      const x = (e.clientX - rect.left) / zoom - dragOffset.x;
      const y = (e.clientY - rect.top) / zoom - dragOffset.y;
      updateElement(selectedElement, { x: Math.max(0, x), y: Math.max(0, y) });
    }
  };

  const handleCanvasMouseUp = () => {
    if (isDragging) {
      setIsDragging(false);
      saveToHistory(elements);
    }
  };

  const handleResize = (elementId, newWidth, newHeight) => {
    updateElement(elementId, { 
      width: Math.max(20, newWidth), 
      height: Math.max(20, newHeight) 
    });
  };

  const saveTemplate = async () => {
    if (!templateName.trim()) {
      toast.error('Please enter a template name');
      return;
    }
    
    setSaving(true);
    try {
      const token = localStorage.getItem('pitchpal_token');
      const res = await fetch(`${API}/api/design/templates`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          name: templateName,
          content_type: 'custom',
          elements: elements,
          background_color: backgroundColor,
          width: canvasSize.width,
          height: canvasSize.height
        })
      });
      
      if (res.ok) {
        toast.success('Template saved!');
        setSaveDialogOpen(false);
        setTemplateName('');
        fetchSavedTemplates();
      } else {
        const error = await res.json();
        toast.error(error.detail || 'Failed to save');
      }
    } catch (error) {
      toast.error('Failed to save template');
    } finally {
      setSaving(false);
    }
  };

  const loadTemplate = (template) => {
    const loadedElements = (template.elements || []).map(el => ({
      ...el,
      id: el.id || Date.now().toString() + Math.random().toString(36).slice(2)
    }));
    setElements(loadedElements);
    setBackgroundColor(template.background_color || '#ffffff');
    setCanvasSize({ width: template.width || 800, height: template.height || 1100 });
    saveToHistory(loadedElements);
    toast.success(`Loaded: ${template.name}`);
  };

  const loadStarterTemplate = (template) => {
    const freshElements = template.elements.map(el => ({
      ...el,
      id: Date.now().toString() + Math.random().toString(36).slice(2, 8)
    }));
    setElements(freshElements);
    setBackgroundColor(template.background_color || '#ffffff');
    setCanvasSize({ width: template.width, height: template.height });
    setActiveColorScheme(null);
    saveToHistory(freshElements);
    toast.success(`Loaded: ${template.name}`);
  };

  const applyScheme = (scheme) => {
    if (elements.length === 0) {
      toast.error('Load a template first, then apply a color scheme');
      return;
    }
    const result = applyColorScheme(elements, scheme);
    const updatedElements = result.elements.map(el => ({
      ...el,
      id: el.id || Date.now().toString() + Math.random().toString(36).slice(2)
    }));
    setElements(updatedElements);
    if (result.backgroundColor) {
      setBackgroundColor(result.backgroundColor);
    }
    setActiveColorScheme(scheme.id);
    saveToHistory(updatedElements);
    toast.success(`Applied: ${scheme.name}`);
  };

  const exportAsPNG = () => {
    toast.info('Export feature - use Print to PDF for best results');
  };

  const handleShare = async () => {
    if (elements.length === 0) {
      toast.error('Add some elements to your design before sharing');
      return;
    }
    setShareDialogOpen(true);
    setShareLink(null);
    setCopied(false);
  };

  const createShareLink = async () => {
    setSharing(true);
    try {
      const res = await shareApi.createShare({
        elements: elements.map(({ _role, ...rest }) => rest),
        background_color: backgroundColor,
        width: canvasSize.width,
        height: canvasSize.height,
        name: templateName || 'Untitled Design'
      });
      const fullUrl = `${window.location.origin}/shared/${res.data.share_id}`;
      setShareLink({ url: fullUrl, ...res.data });
      toast.success('Share link created!');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to create share link');
    } finally {
      setSharing(false);
    }
  };

  const copyShareLink = () => {
    if (shareLink?.url) {
      navigator.clipboard.writeText(shareLink.url);
      setCopied(true);
      toast.success('Link copied to clipboard');
      setTimeout(() => setCopied(false), 3000);
    }
  };

  const fetchMyShares = async () => {
    try {
      const res = await shareApi.getMyShares();
      setMyShares(res.data);
    } catch (err) {
      console.error('Error fetching shares:', err);
    }
  };

  const revokeShare = async (shareId) => {
    try {
      await shareApi.revokeShare(shareId);
      setMyShares(prev => prev.filter(s => s.share_id !== shareId));
      toast.success('Share link revoked');
    } catch (err) {
      toast.error('Failed to revoke link');
    }
  };

  if (authLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <Loader2 className="w-8 h-8 animate-spin text-primary" />
      </div>
    );
  }

  // Mobile notice for Design Studio
  const MobileNotice = () => (
    <div className="md:hidden min-h-screen pt-24 pb-16 bg-alabaster flex flex-col items-center justify-center px-6 text-center" data-testid="design-studio-mobile-notice">
      <div className="w-20 h-20 bg-primary-100 rounded-2xl flex items-center justify-center mb-6">
        <Sparkles className="w-10 h-10 text-primary" />
      </div>
      <h2 className="font-heading text-2xl font-bold text-[#0A0A0A] mb-3">
        Promote & Create
      </h2>
      <p className="text-[#5C6B67] mb-6 max-w-sm">
        The design canvas works best on a desktop or laptop. Please switch to a larger screen to create your fundraising materials.
      </p>
      <Button onClick={() => navigate('/dashboard')} className="bg-primary hover:bg-primary-800 text-white">
        Back to Dashboard
      </Button>
    </div>
  );

  const selectedEl = elements.find(el => el.id === selectedElement);

  return (
    <>
      <MobileNotice />
      <div className="min-h-screen bg-stone-100 pt-16 hidden md:block" data-testid="design-studio-page">
      {/* Toolbar */}
      <div className="fixed top-16 left-0 right-0 bg-white border-b z-40 px-4 py-2">
        <div className="max-w-screen-2xl mx-auto flex items-center justify-between">
          <div className="flex items-center gap-2">
            <h1 className="font-semibold text-stone-900 mr-4">Promote & Create</h1>
            
            {/* Add Elements */}
            <div className="flex items-center gap-1 border-r pr-4 mr-4">
              <Button variant="ghost" size="sm" onClick={() => addElement('text')} data-testid="add-text-btn">
                <Type className="w-4 h-4 mr-1" /> Text
              </Button>
              <Button variant="ghost" size="sm" onClick={() => addElement('shape')} data-testid="add-shape-btn">
                <Square className="w-4 h-4 mr-1" /> Shape
              </Button>
              <Button variant="ghost" size="sm" onClick={() => addElement('divider')} data-testid="add-divider-btn">
                <Minus className="w-4 h-4 mr-1" /> Divider
              </Button>
            </div>
            
            {/* Undo/Redo */}
            <div className="flex items-center gap-1 border-r pr-4 mr-4">
              <Button variant="ghost" size="sm" onClick={undo} disabled={historyIndex <= 0} data-testid="undo-btn">
                <Undo className="w-4 h-4" />
              </Button>
              <Button variant="ghost" size="sm" onClick={redo} disabled={historyIndex >= history.length - 1} data-testid="redo-btn">
                <Redo className="w-4 h-4" />
              </Button>
            </div>
            
            {/* Zoom */}
            <div className="flex items-center gap-1">
              <Button variant="ghost" size="sm" onClick={() => setZoom(Math.max(0.25, zoom - 0.1))} data-testid="zoom-out-btn">
                <ZoomOut className="w-4 h-4" />
              </Button>
              <span className="text-sm text-stone-600 w-12 text-center" data-testid="zoom-level">{Math.round(zoom * 100)}%</span>
              <Button variant="ghost" size="sm" onClick={() => setZoom(Math.min(2, zoom + 0.1))} data-testid="zoom-in-btn">
                <ZoomIn className="w-4 h-4" />
              </Button>
            </div>
          </div>
          
          <div className="flex items-center gap-2">
            <Button variant="outline" size="sm" onClick={handleShare} data-testid="share-design-btn">
              <Share2 className="w-4 h-4 mr-1" /> Share
            </Button>
            <Button variant="outline" size="sm" onClick={() => setSaveDialogOpen(true)} data-testid="save-template-btn">
              <Save className="w-4 h-4 mr-1" /> Save
            </Button>
            <Button variant="outline" size="sm" onClick={exportAsPNG} data-testid="export-btn">
              <Download className="w-4 h-4 mr-1" /> Export
            </Button>
          </div>
        </div>
      </div>

      <div className="flex pt-12">
        {/* Left Sidebar - Images */}
        <div className="w-72 bg-white border-r h-[calc(100vh-7rem)] overflow-y-auto fixed left-0">
          <Tabs defaultValue="starters" className="p-4">
            <TabsList className="w-full grid grid-cols-2 mb-1">
              <TabsTrigger value="starters" className="text-xs">Templates</TabsTrigger>
              <TabsTrigger value="colors" className="text-xs">Colors</TabsTrigger>
            </TabsList>
            <TabsList className="w-full grid grid-cols-3">
              <TabsTrigger value="stock" className="text-xs">Stock</TabsTrigger>
              <TabsTrigger value="uploads" className="text-xs">My Images</TabsTrigger>
              <TabsTrigger value="saved" className="text-xs">Saved</TabsTrigger>
            </TabsList>
            
            <TabsContent value="starters" className="mt-4 space-y-4">
              {/* Guided purpose prompt */}
              <div className="bg-primary/5 border border-primary/10 rounded-lg p-3">
                <p className="text-xs font-medium text-primary mb-1">What are you creating?</p>
                <p className="text-[10px] text-[#5C6B67]">Pick a template designed for your fundraising goal.</p>
              </div>

              <p className="text-[10px] text-stone-400 font-bold uppercase tracking-wider">Fundraising & Events</p>
              {STARTER_TEMPLATES.filter(t => ['starter-fundraising-flyer', 'starter-donation-appeal'].includes(t.id)).map((template) => (
                <div
                  key={template.id}
                  data-testid={`starter-template-${template.id}`}
                  className="group p-3 border rounded-lg cursor-pointer hover:border-primary hover:bg-primary/5 transition-all"
                  onClick={() => loadStarterTemplate(template)}
                >
                  <div className="flex items-start gap-3">
                    <div className="w-10 h-10 rounded-lg bg-primary/10 flex items-center justify-center flex-shrink-0">
                      <LayoutTemplate className="w-5 h-5 text-primary" />
                    </div>
                    <div className="min-w-0">
                      <p className="font-medium text-sm text-stone-900 group-hover:text-primary truncate">{template.name}</p>
                      <p className="text-xs text-stone-500 mt-0.5">{template.description}</p>
                    </div>
                  </div>
                </div>
              ))}

              <p className="text-[10px] text-stone-400 font-bold uppercase tracking-wider pt-2">Donor Communication</p>
              {STARTER_TEMPLATES.filter(t => ['starter-thank-you-card', 'starter-newsletter'].includes(t.id)).map((template) => (
                <div
                  key={template.id}
                  data-testid={`starter-template-${template.id}`}
                  className="group p-3 border rounded-lg cursor-pointer hover:border-primary hover:bg-primary/5 transition-all"
                  onClick={() => loadStarterTemplate(template)}
                >
                  <div className="flex items-start gap-3">
                    <div className="w-10 h-10 rounded-lg bg-blue-50 flex items-center justify-center flex-shrink-0">
                      <LayoutTemplate className="w-5 h-5 text-blue-600" />
                    </div>
                    <div className="min-w-0">
                      <p className="font-medium text-sm text-stone-900 group-hover:text-primary truncate">{template.name}</p>
                      <p className="text-xs text-stone-500 mt-0.5">{template.description}</p>
                    </div>
                  </div>
                </div>
              ))}

              <p className="text-[10px] text-stone-400 font-bold uppercase tracking-wider pt-2">Reports & Social</p>
              {STARTER_TEMPLATES.filter(t => ['starter-social-media', 'starter-annual-report'].includes(t.id)).map((template) => (
                <div
                  key={template.id}
                  data-testid={`starter-template-${template.id}`}
                  className="group p-3 border rounded-lg cursor-pointer hover:border-primary hover:bg-primary/5 transition-all"
                  onClick={() => loadStarterTemplate(template)}
                >
                  <div className="flex items-start gap-3">
                    <div className="w-10 h-10 rounded-lg bg-amber-50 flex items-center justify-center flex-shrink-0">
                      <LayoutTemplate className="w-5 h-5 text-amber-600" />
                    </div>
                    <div className="min-w-0">
                      <p className="font-medium text-sm text-stone-900 group-hover:text-primary truncate">{template.name}</p>
                      <p className="text-xs text-stone-500 mt-0.5">{template.description}</p>
                    </div>
                  </div>
                </div>
              ))}
            </TabsContent>

            <TabsContent value="colors" className="mt-4 space-y-3">
              <p className="text-xs text-stone-500 font-medium uppercase tracking-wide">Color Schemes</p>
              <p className="text-xs text-stone-400">Load a template first, then pick a scheme to restyle it</p>
              {COLOR_SCHEMES.map((scheme) => (
                <div
                  key={scheme.id}
                  data-testid={`color-scheme-${scheme.id}`}
                  className={`p-3 border rounded-lg cursor-pointer hover:border-primary transition-all ${activeColorScheme === scheme.id ? 'border-primary bg-primary/5 ring-1 ring-primary' : ''}`}
                  onClick={() => applyScheme(scheme)}
                >
                  <div className="flex items-center gap-3">
                    <div className="flex gap-1">
                      <div className="w-6 h-6 rounded-full border" style={{ backgroundColor: scheme.primary }} />
                      <div className="w-6 h-6 rounded-full border" style={{ backgroundColor: scheme.secondary }} />
                      <div className="w-6 h-6 rounded-full border" style={{ backgroundColor: scheme.accent }} />
                    </div>
                    <span className="text-sm font-medium text-stone-800">{scheme.name}</span>
                  </div>
                </div>
              ))}
            </TabsContent>
            
            <TabsContent value="stock" className="mt-4 space-y-4">
              <div className="flex gap-2">
                <Input
                  placeholder="Search images..."
                  value={stockSearchQuery}
                  onChange={(e) => setStockSearchQuery(e.target.value)}
                  onKeyDown={(e) => e.key === 'Enter' && searchStockImages()}
                  data-testid="stock-search-input"
                />
                <Button size="sm" onClick={searchStockImages} disabled={searchingStock} data-testid="stock-search-btn">
                  {searchingStock ? <Loader2 className="w-4 h-4 animate-spin" /> : <Search className="w-4 h-4" />}
                </Button>
              </div>
              
              <div className="grid grid-cols-2 gap-2">
                {stockImages.map((img) => (
                  <div
                    key={img.id}
                    className="aspect-video rounded overflow-hidden cursor-pointer hover:ring-2 ring-primary transition-all"
                    onClick={() => addElement('image', img.url)}
                  >
                    <img src={img.thumb || img.url} alt={img.description} className="w-full h-full object-cover" />
                  </div>
                ))}
              </div>
              
              {stockImages.length === 0 && (
                <p className="text-sm text-stone-500 text-center py-4">
                  Search for stock images to add to your design
                </p>
              )}
            </TabsContent>
            
            <TabsContent value="uploads" className="mt-4 space-y-4">
              {isPro ? (
                <>
                  <label className="flex items-center justify-center gap-2 p-4 border-2 border-dashed rounded-lg cursor-pointer hover:bg-stone-50" data-testid="upload-image-area">
                    <input
                      type="file"
                      accept="image/*"
                      className="hidden"
                      onChange={(e) => e.target.files[0] && uploadImage(e.target.files[0])}
                      disabled={uploading}
                      data-testid="upload-image-input"
                    />
                {uploading ? (
                  <Loader2 className="w-5 h-5 animate-spin" />
                ) : (
                  <>
                    <Upload className="w-5 h-5" />
                    <span className="text-sm">Upload Image</span>
                  </>
                )}
              </label>
              
              <div className="grid grid-cols-2 gap-2">
                {myImages.map((img) => (
                  <div
                    key={img.id}
                    className="aspect-video rounded overflow-hidden cursor-pointer hover:ring-2 ring-primary transition-all relative group"
                    onClick={() => addElement('image', `${API}${img.url}`)}
                  >
                    <img src={`${API}${img.url}`} alt={img.filename} className="w-full h-full object-cover" />
                  </div>
                ))}
              </div>
              
              {myImages.length === 0 && (
                <p className="text-sm text-stone-500 text-center py-4">
                  Upload your own images to use in designs
                </p>
              )}
                </>
              ) : (
                <div className="text-center py-6">
                  <Lock className="w-8 h-8 text-stone-400 mx-auto mb-3" />
                  <p className="text-sm text-stone-600 mb-2 font-medium">Pro Feature</p>
                  <p className="text-xs text-stone-500 mb-4">Upload your own images with a Pro plan</p>
                  <Button size="sm" onClick={() => navigate('/pricing')} className="bg-primary hover:bg-primary-800 text-white" data-testid="upgrade-uploads-btn">
                    Upgrade to Pro
                  </Button>
                </div>
              )}
            </TabsContent>
            
            <TabsContent value="saved" className="mt-4 space-y-2">
              {savedTemplates.map((template) => (
                <div
                  key={template.id}
                  className="p-3 border rounded-lg cursor-pointer hover:bg-stone-50"
                  onClick={() => loadTemplate(template)}
                >
                  <p className="font-medium text-sm">{template.name}</p>
                  <p className="text-xs text-stone-500">{template.elements?.length || 0} elements</p>
                </div>
              ))}
              
              {savedTemplates.length === 0 && (
                <p className="text-sm text-stone-500 text-center py-4">
                  Save your designs to access them here
                </p>
              )}
            </TabsContent>
          </Tabs>
        </div>

        {/* Canvas Area */}
        <div className="flex-1 ml-72 mr-72 p-8 flex justify-center overflow-auto">
          <div
            ref={canvasRef}
            className="bg-white shadow-xl relative"
            data-testid="design-canvas"
            style={{
              width: canvasSize.width * zoom,
              height: canvasSize.height * zoom,
              backgroundColor: backgroundColor,
              transform: `scale(1)`,
              transformOrigin: 'top center'
            }}
            onMouseMove={handleCanvasMouseMove}
            onMouseUp={handleCanvasMouseUp}
            onMouseLeave={handleCanvasMouseUp}
            onClick={(e) => e.target === canvasRef.current && setSelectedElement(null)}
          >
            {elements.map((element) => (
              <div
                key={element.id}
                data-testid={`canvas-element-${element.type}`}
                className={`absolute cursor-move ${selectedElement === element.id ? 'ring-2 ring-primary ring-offset-2' : ''}`}
                style={{
                  left: element.x * zoom,
                  top: element.y * zoom,
                  width: element.width * zoom,
                  height: element.height * zoom,
                  backgroundColor: element.style?.backgroundColor || 'transparent',
                  borderRadius: (element.style?.borderRadius || 0) * zoom,
                }}
                onMouseDown={(e) => handleCanvasMouseDown(e, element.id)}
              >
                {element.type === 'image' && element.content && (
                  <img
                    src={element.content}
                    alt=""
                    className="w-full h-full object-cover"
                    style={{ borderRadius: (element.style?.borderRadius || 0) * zoom }}
                    draggable={false}
                  />
                )}
                
                {element.type === 'text' && (
                  <div
                    contentEditable
                    suppressContentEditableWarning
                    className="w-full h-full outline-none overflow-hidden"
                    style={{
                      color: element.style?.color || '#1a202c',
                      fontSize: (element.style?.fontSize || 16) * zoom,
                      fontWeight: element.style?.fontWeight || 'normal',
                      textAlign: element.style?.textAlign || 'left',
                    }}
                    onBlur={(e) => updateElement(element.id, { content: e.target.innerText })}
                  >
                    {element.content}
                  </div>
                )}
                
                {element.type === 'divider' && (
                  <div
                    className="w-full h-full"
                    style={{ backgroundColor: element.style?.color || '#e2e8f0' }}
                  />
                )}
                
                {/* Resize Handle */}
                {selectedElement === element.id && (
                  <div
                    className="absolute bottom-0 right-0 w-4 h-4 bg-primary cursor-se-resize"
                    style={{ transform: 'translate(50%, 50%)' }}
                    onMouseDown={(e) => {
                      e.stopPropagation();
                      const startX = e.clientX;
                      const startY = e.clientY;
                      const startWidth = element.width;
                      const startHeight = element.height;
                      
                      const handleMouseMove = (moveE) => {
                        const dx = (moveE.clientX - startX) / zoom;
                        const dy = (moveE.clientY - startY) / zoom;
                        handleResize(element.id, startWidth + dx, startHeight + dy);
                      };
                      
                      const handleMouseUp = () => {
                        document.removeEventListener('mousemove', handleMouseMove);
                        document.removeEventListener('mouseup', handleMouseUp);
                        saveToHistory(elements);
                      };
                      
                      document.addEventListener('mousemove', handleMouseMove);
                      document.addEventListener('mouseup', handleMouseUp);
                    }}
                  />
                )}
              </div>
            ))}
          </div>
        </div>

        {/* Right Sidebar - Properties */}
        <div className="w-72 bg-white border-l h-[calc(100vh-7rem)] overflow-y-auto fixed right-0">
          <div className="p-4 space-y-6">
            {/* Canvas Properties */}
            <div>
              <h3 className="font-medium text-sm mb-3">Canvas</h3>
              <div className="space-y-3">
                <div>
                  <Label className="text-xs">Background Color</Label>
                  <div className="flex flex-wrap gap-1 mt-1">
                    {PRESET_COLORS.map((color) => (
                      <button
                        key={color}
                        className={`w-6 h-6 rounded border ${backgroundColor === color ? 'ring-2 ring-primary ring-offset-1' : ''}`}
                        style={{ backgroundColor: color }}
                        onClick={() => setBackgroundColor(color)}
                      />
                    ))}
                  </div>
                </div>
              </div>
            </div>
            
            {/* Selected Element Properties */}
            {selectedEl && (
              <div className="border-t pt-4">
                <div className="flex items-center justify-between mb-3">
                  <h3 className="font-medium text-sm">
                    {ELEMENT_TYPES[selectedEl.type]?.label || 'Element'}
                  </h3>
                  <div className="flex items-center gap-1">
                    <Button variant="ghost" size="sm" onClick={() => duplicateElement(selectedEl.id)} data-testid="duplicate-element-btn">
                      <Copy className="w-4 h-4 text-stone-600" />
                    </Button>
                    <Button variant="ghost" size="sm" onClick={() => deleteElement(selectedEl.id)} data-testid="delete-element-btn">
                      <Trash2 className="w-4 h-4 text-red-500" />
                    </Button>
                  </div>
                </div>
                
                <div className="space-y-3">
                  {/* Position */}
                  <div className="grid grid-cols-2 gap-2">
                    <div>
                      <Label className="text-xs">X</Label>
                      <Input
                        type="number"
                        value={Math.round(selectedEl.x)}
                        onChange={(e) => updateElement(selectedEl.id, { x: parseInt(e.target.value) || 0 })}
                        className="h-8"
                      />
                    </div>
                    <div>
                      <Label className="text-xs">Y</Label>
                      <Input
                        type="number"
                        value={Math.round(selectedEl.y)}
                        onChange={(e) => updateElement(selectedEl.id, { y: parseInt(e.target.value) || 0 })}
                        className="h-8"
                      />
                    </div>
                  </div>
                  
                  {/* Size */}
                  <div className="grid grid-cols-2 gap-2">
                    <div>
                      <Label className="text-xs">Width</Label>
                      <Input
                        type="number"
                        value={Math.round(selectedEl.width)}
                        onChange={(e) => updateElement(selectedEl.id, { width: parseInt(e.target.value) || 50 })}
                        className="h-8"
                      />
                    </div>
                    <div>
                      <Label className="text-xs">Height</Label>
                      <Input
                        type="number"
                        value={Math.round(selectedEl.height)}
                        onChange={(e) => updateElement(selectedEl.id, { height: parseInt(e.target.value) || 50 })}
                        className="h-8"
                      />
                    </div>
                  </div>
                  
                  {/* Text styling */}
                  {selectedEl.type === 'text' && (
                    <>
                      <div>
                        <Label className="text-xs">Font Size</Label>
                        <Input
                          type="number"
                          value={selectedEl.style?.fontSize || 16}
                          onChange={(e) => updateElement(selectedEl.id, { 
                            style: { ...selectedEl.style, fontSize: parseInt(e.target.value) || 16 } 
                          })}
                          className="h-8"
                        />
                      </div>
                      
                      <div>
                        <Label className="text-xs">Text Color</Label>
                        <div className="flex flex-wrap gap-1 mt-1">
                          {PRESET_COLORS.slice(0, 10).map((color) => (
                            <button
                              key={color}
                              className={`w-5 h-5 rounded border ${selectedEl.style?.color === color ? 'ring-2 ring-primary ring-offset-1' : ''}`}
                              style={{ backgroundColor: color }}
                              onClick={() => updateElement(selectedEl.id, { 
                                style: { ...selectedEl.style, color } 
                              })}
                            />
                          ))}
                        </div>
                      </div>
                    </>
                  )}
                  
                  {/* Shape/Divider color */}
                  {(selectedEl.type === 'shape' || selectedEl.type === 'divider') && (
                    <div>
                      <Label className="text-xs">Fill Color</Label>
                      <div className="flex flex-wrap gap-1 mt-1">
                        {PRESET_COLORS.map((color) => (
                          <button
                            key={color}
                            className={`w-5 h-5 rounded border ${selectedEl.style?.backgroundColor === color ? 'ring-2 ring-primary ring-offset-1' : ''}`}
                            style={{ backgroundColor: color }}
                            onClick={() => updateElement(selectedEl.id, { 
                              style: { ...selectedEl.style, backgroundColor: color } 
                            })}
                          />
                        ))}
                      </div>
                    </div>
                  )}
                  
                  {/* Layer controls */}
                  <div className="flex gap-2">
                    <Button variant="outline" size="sm" onClick={() => bringForward(selectedEl.id)} className="flex-1" data-testid="layer-forward-btn">
                      <ChevronUp className="w-4 h-4 mr-1" /> Forward
                    </Button>
                    <Button variant="outline" size="sm" onClick={() => sendBackward(selectedEl.id)} className="flex-1" data-testid="layer-backward-btn">
                      <ChevronDown className="w-4 h-4 mr-1" /> Back
                    </Button>
                  </div>
                  
                  {/* Duplicate */}
                  <Button variant="outline" size="sm" onClick={() => duplicateElement(selectedEl.id)} className="w-full" data-testid="duplicate-element-full-btn">
                    <Copy className="w-4 h-4 mr-1" /> Duplicate Element
                  </Button>
                </div>
              </div>
            )}
            
            {!selectedEl && (
              <p className="text-sm text-stone-500 text-center py-8">
                Click an element to edit its properties
              </p>
            )}
          </div>
        </div>
      </div>

      {/* Save Template Dialog */}
      <Dialog open={saveDialogOpen} onOpenChange={setSaveDialogOpen}>
        <DialogContent className="max-w-sm bg-white">
          <DialogHeader>
            <DialogTitle>Save Template</DialogTitle>
          </DialogHeader>
          
          <div>
            <Label>Template Name</Label>
            <Input
              value={templateName}
              onChange={(e) => setTemplateName(e.target.value)}
              placeholder="My Custom Template"
              className="mt-1"
              data-testid="template-name-input"
            />
          </div>
          
          <DialogFooter>
            <Button variant="outline" onClick={() => setSaveDialogOpen(false)} data-testid="save-cancel-btn">Cancel</Button>
            <Button onClick={saveTemplate} disabled={saving || !templateName.trim()} data-testid="save-confirm-btn">
              {saving ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <Save className="w-4 h-4 mr-2" />}
              Save
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Share Dialog */}
      <Dialog open={shareDialogOpen} onOpenChange={(open) => { setShareDialogOpen(open); if (!open) setShowMyShares(false); }}>
        <DialogContent className="max-w-md bg-white">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Share2 className="w-5 h-5 text-primary" /> Share Design
            </DialogTitle>
          </DialogHeader>
          
          {!shareLink ? (
            <div className="space-y-4">
              <div>
                <Label>Design Name</Label>
                <Input
                  value={templateName}
                  onChange={(e) => setTemplateName(e.target.value)}
                  placeholder="Name your design"
                  className="mt-1"
                  data-testid="share-name-input"
                />
              </div>
              <div className="bg-stone-50 rounded-lg p-3 text-sm text-stone-600 space-y-1">
                <p className="flex items-center gap-2"><Eye className="w-4 h-4" /> View only — viewers cannot edit</p>
                <p className="flex items-center gap-2"><Clock className="w-4 h-4" /> Link expires in 30 days</p>
              </div>
              <Button
                onClick={createShareLink}
                disabled={sharing}
                className="w-full bg-primary hover:bg-primary-800 text-white"
                data-testid="create-share-link-btn"
              >
                {sharing ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <Link2 className="w-4 h-4 mr-2" />}
                Generate Share Link
              </Button>
            </div>
          ) : (
            <div className="space-y-4">
              <div className="bg-green-50 border border-green-200 rounded-lg p-3">
                <p className="text-sm font-medium text-green-800 mb-2 flex items-center gap-1">
                  <Check className="w-4 h-4" /> Link created!
                </p>
                <div className="flex gap-2">
                  <Input
                    value={shareLink.url}
                    readOnly
                    className="text-xs bg-white"
                    data-testid="share-link-url"
                  />
                  <Button
                    size="sm"
                    onClick={copyShareLink}
                    variant={copied ? "default" : "outline"}
                    className={copied ? "bg-green-600 text-white" : ""}
                    data-testid="copy-share-link-btn"
                  >
                    {copied ? <Check className="w-4 h-4" /> : <Copy className="w-4 h-4" />}
                  </Button>
                </div>
              </div>
              <p className="text-xs text-stone-500 flex items-center gap-1">
                <Clock className="w-3 h-3" /> Expires: {new Date(shareLink.expires_at).toLocaleDateString('en-US', { month: 'long', day: 'numeric', year: 'numeric' })}
              </p>
            </div>
          )}

          {/* My Shares toggle */}
          <div className="border-t pt-3 mt-2">
            <button
              onClick={() => { setShowMyShares(!showMyShares); if (!showMyShares) fetchMyShares(); }}
              className="text-sm text-primary hover:underline"
              data-testid="toggle-my-shares-btn"
            >
              {showMyShares ? 'Hide my shared links' : 'View my shared links'}
            </button>
            
            {showMyShares && (
              <div className="mt-3 space-y-2 max-h-48 overflow-y-auto">
                {myShares.length === 0 ? (
                  <p className="text-xs text-stone-400 text-center py-2">No shared links yet</p>
                ) : (
                  myShares.map((share) => (
                    <div key={share.share_id} className={`flex items-center justify-between p-2 rounded-lg text-sm ${share.is_expired ? 'bg-stone-50 opacity-60' : 'bg-stone-50'}`} data-testid={`shared-link-item-${share.share_id}`}>
                      <div className="min-w-0 flex-1">
                        <p className="font-medium text-stone-800 truncate text-xs">{share.name}</p>
                        <p className="text-xs text-stone-500 flex items-center gap-2">
                          <span className="flex items-center gap-0.5"><Eye className="w-3 h-3" />{share.views}</span>
                          {share.is_expired && <span className="text-red-500">Expired</span>}
                        </p>
                      </div>
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => revokeShare(share.share_id)}
                        className="text-red-500 hover:text-red-700 hover:bg-red-50 h-7 w-7 p-0"
                        data-testid={`revoke-share-${share.share_id}`}
                      >
                        <Trash className="w-3.5 h-3.5" />
                      </Button>
                    </div>
                  ))
                )}
              </div>
            )}
          </div>
        </DialogContent>
      </Dialog>
    </div>
    </>
  );
}
